import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  FoodMenuWhiteIcon,
  TruckIcon,
  FrameIcon,
  GlobalIcon,
  MapPinWhiteIcon,
  DonateHeartIcon,
  BellIcon,
  BellSuccessIcon,
} from 'assets/svg';
import styled from 'styled-components';
import Button from 'components/Button';
import { Row, Col } from 'components/Layout/RowColLayout';
import { breakpoints } from 'constant/style';
import { Modal, Rate } from 'antd';
import { getServiceOptionsLabels, getFilledStars } from 'utils/common';
import {
  MICROSITE_BUTTON_TYPE,
  FontsList,
  HOME_PAGE_DEFAULT_BUTTON,
  getMicrositeSubscibrKey,
} from 'constant/constants';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import { MicrositeTheme, ThirdPartyLink, DesktopViewWrapProps, SubscribeButtonWrapProps } from 'utils/types';
import PoweredByMomos from '../PoweredByMomos';
import SocialMediaLinks from 'components/SocialMediaLinks';
import SubscribeModal from 'components/SubscribeModal';
import UnsubscribeModal from 'components/UnsubscribeModal';
import { useRouter } from 'next/router';

const BUTTON_ICONS: Record<string, ReactNode> = {
  [HOME_PAGE_DEFAULT_BUTTON.MENU]: <FoodMenuWhiteIcon />,
  [HOME_PAGE_DEFAULT_BUTTON.DELIVERY]: <TruckIcon />,
  [HOME_PAGE_DEFAULT_BUTTON.RESERVATION]: <FrameIcon />,
  [HOME_PAGE_DEFAULT_BUTTON.SURVEY]: <GlobalIcon />,
  [HOME_PAGE_DEFAULT_BUTTON.SURVEY]: <DonateHeartIcon />,
};

type Button = {
  name: string;
  type: string;
  value: string;
  status: string;
};

interface DesktopViewProps {
  onClickSeeLocations: () => void;
  theme: MicrositeTheme;
  brandName: string;
  serviceOptions: string[];
  outletCount: number;
  thirdPartyLinks: ThirdPartyLink[];
  logo: string;
  reviewConfigIcon: ReactNode;
  reviewsData: any;
  buttons: Button[];
  navigateTo: (name: string) => void;
  isFoodDelivery: boolean;
  isReservation: boolean;
  isMenu: boolean;
  showBrandName: boolean;
  isSurvey: boolean;
  micrositeData?: any;
  slug: string;
}

const DesktopView = ({
  onClickSeeLocations,
  theme,
  brandName,
  serviceOptions,
  outletCount,
  logo,
  reviewConfigIcon,
  reviewsData,
  buttons,
  navigateTo,
  isFoodDelivery,
  isReservation,
  isMenu,
  showBrandName,
  isSurvey,
  micrositeData,
  slug,
}: DesktopViewProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSessionSubscribed, setIsSessionSubscribed] = useState(null);
  const [isUnsubscribed, setUnsubscribed] = useState(true);
  const router = useRouter();
  const { subscribePopUp } = router.query;
  useEffect(() => {
    if (slug) {
      const brandMicrositeKey = getMicrositeSubscibrKey(slug);
      const brandMicrositeSubscribeSessionData: any = localStorage?.getItem(brandMicrositeKey);
      if (!brandMicrositeSubscribeSessionData) {
        setIsSessionSubscribed(null);
      } else {
        setIsSessionSubscribed(JSON.parse(brandMicrositeSubscribeSessionData));
      }
    }
  }, [slug]);

  const {
    fontColor,
    backgroundColor,
    highlightColor,
    fontType,
    titleFontType,
    reviewStarColor,
    headerBgColor,
    headerTextColor,
  } = theme || {};

  const isActiveLink = (name: string, status: string) => {
    switch (name) {
      case HOME_PAGE_DEFAULT_BUTTON.MENU:
        return isMenu && status;

      case HOME_PAGE_DEFAULT_BUTTON.DELIVERY:
        return isFoodDelivery && status;

      case HOME_PAGE_DEFAULT_BUTTON.RESERVATION:
        return isReservation && status;

      case HOME_PAGE_DEFAULT_BUTTON.SURVEY:
        return isSurvey && status;

      default:
        return status;
    }
  };

  const { micrositeIsSubscribe } = micrositeData || {};
  const micrositeSubscribeButtonConfig = micrositeData?.micrositeSubscribeButtonConfig;

  const showModal = (flag = true) => {
    setIsModalVisible(flag);
  };

  const subscribeModalTheme = useMemo(() => {
    const micrositeSubscribeModalConfig = micrositeData?.micrositeSubscribeModalConfig;
    const theme = {
      subscribeModalFontType: micrositeSubscribeModalConfig?.fontType,
      subscribeModalFontColor: micrositeSubscribeModalConfig?.fontColor,
      subscribeModalHighlightColor: micrositeSubscribeModalConfig?.highlightColor,
      subscribeModalFooterButtonBackgroundColor: micrositeSubscribeModalConfig?.footerButtonBackgroundColor,
      subscribeModalFooterButtonFontType: micrositeSubscribeModalConfig?.footerButtonFontType,
    };

    const subscribeData = {
      SubscribeModalConfigTitle: micrositeSubscribeModalConfig?.title,
      SubscribeModalConfigBody: micrositeSubscribeModalConfig?.body,
      subscribeModalFields: micrositeSubscribeModalConfig?.fields,
      SubscribeModalConfigFooterButtonTitle: micrositeSubscribeModalConfig?.footerButtonTitle,
      voucherDetails: micrositeSubscribeModalConfig?.voucherDetails,
      unSubscribeModalDescription: micrositeSubscribeModalConfig?.unSubscribeModalConfig?.description,
      unSubscribeModalFooterButtonTitle: micrositeSubscribeModalConfig?.unSubscribeModalConfig?.footerButtonTitle,
      unSubscribeModalTitle: micrositeSubscribeModalConfig?.unSubscribeModalConfig?.title,
      hideLogo: micrositeSubscribeModalConfig?.hideLogo === undefined ? true : micrositeSubscribeModalConfig?.hideLogo,
    };
    return { theme, subscribeData };
  }, [micrositeData]);

  useEffect(() => {
    const handleModalChange = () => {
      setIsModalVisible(false);
    };
    setIsModalVisible(
      subscribePopUp === 'true'
        ? true
        : micrositeData?.micrositeSubscribeModalConfig?.modalReappear?.onPageLoad &&
            !isSessionSubscribed &&
            isUnsubscribed &&
            subscribePopUp !== 'false',
    );

    router.events.on('routeChangeComplete', handleModalChange);
    return () => {
      router.events.off('routeChangeComplete', handleModalChange);
    };
  }, [
    micrositeData?.micrositeSubscribeModalConfig?.modalReappear?.onPageLoad,
    isSessionSubscribed,
    isUnsubscribed,
    subscribePopUp,
  ]);

  const onModalOpen = () => {
    if (!isSessionSubscribed) {
      ga.event(TRACKING.HOME.SUBSCRIBE.SUBSCRIBE);
    } else {
      ga.event(TRACKING.HOME.UNSUBSCRIBE.UNSUBSCRIBE);
    }
    showModal(true);
  };

  return (
    <DesktopViewWrap
      $fontColor={fontColor}
      $fontType={fontType}
      $headerBgColor={headerBgColor}
      $headerTextColor={headerTextColor}
      $backgroundColor={backgroundColor}
    >
      {micrositeIsSubscribe && isModalVisible && (
        <>
          <Modal
            title=""
            visible={isModalVisible}
            closable={true}
            footer={null}
            onCancel={() => {
              if (isSessionSubscribed) {
                ga.event(TRACKING.HOME.SUBSCRIBE.UNSUBSCRIBE_MODAL_CLOSE);
              } else {
                ga.event(TRACKING.HOME.SUBSCRIBE.MODAL_CLOSE);
              }
              showModal(false);
            }}
          >
            {!isSessionSubscribed ? (
              <SubscribeModal
                onClose={() => showModal(false)}
                slug={slug}
                theme={subscribeModalTheme.theme}
                subscribeData={subscribeModalTheme.subscribeData}
                loading={false}
                brandName={brandName}
                setIsSessionSubscribed={setIsSessionSubscribed}
                logo={logo}
                brandRegion={micrositeData.region}
              />
            ) : (
              <UnsubscribeModal
                onClose={() => showModal(false)}
                slug={slug}
                theme={subscribeModalTheme.theme}
                subscribeData={subscribeModalTheme.subscribeData}
                loading={false}
                setIsSessionSubscribed={setIsSessionSubscribed}
                logo={logo}
                email={(isSessionSubscribed as Record<string, any>)?.email}
                setUnsubscribed={setUnsubscribed}
              />
            )}
          </Modal>
        </>
      )}
      <div className="hero-container">
        {micrositeIsSubscribe && (
          <MicrositeSubscribeButton
            fontType={micrositeSubscribeButtonConfig?.fontType}
            fontColor={micrositeSubscribeButtonConfig?.fontColor}
            highlightColor={micrositeSubscribeButtonConfig?.highlightColor}
          >
            <div className={'btn-container'} onClick={onModalOpen}>
              {!isSessionSubscribed ? <BellIcon /> : <BellSuccessIcon />}

              <p className="flex btn-text align-items-center text-center mb-0">
                {!isSessionSubscribed ? micrositeSubscribeButtonConfig?.title || 'Subscribe' : 'Subscribed'}
              </p>
            </div>
          </MicrositeSubscribeButton>
        )}
        {logo && <img src={logo} className="microsite-desk-logo" alt={`${brandName}`} />}
        {showBrandName && (
          <h2 className="microsite-title" style={{ fontFamily: titleFontType || fontType }}>
            {brandName}
          </h2>
        )}

        {reviewsData?.count > 0 && (
          <div className="rating-wrap">
            {reviewConfigIcon}
            <div className="header-text body-text mr-15">{reviewsData?.avgRating}</div>
            <div className="flex mr-15">
              <Rate
                disabled
                allowHalf
                value={getFilledStars(reviewsData?.avgRating)}
                style={{ color: reviewStarColor, fontSize: 14 }}
              />
            </div>
            <div className="header-text body-text">{reviewsData?.count} Reviews</div>
          </div>
        )}
        {outletCount > 0 && (
          <div className="header-text location-text body-text" onClick={onClickSeeLocations}>
            <MapPinWhiteIcon />
            See Locations ({outletCount} Locations)
          </div>
        )}
        <div className="service-options-wrap mb-20">
          {getServiceOptionsLabels(serviceOptions)?.map((option, key) => (
            <div key={key} className="header-text service-option body-text">
              {option}
            </div>
          ))}
        </div>
      </div>
      <div className="links-wrap">
        <Row gutter={[16, 16]}>
          {buttons.map(({ name, type, value, status }, key) => (
            <React.Fragment key={key}>
              {isActiveLink(name, status) && (
                <Col lg={{ span: 12, offset: 6 }} xs={24}>
                  {type === MICROSITE_BUTTON_TYPE.DEFAULT ? (
                    <HighlightButton
                      type="default"
                      ghost
                      block
                      className="menu-button"
                      $highlightColor={highlightColor}
                      $backgroundColor={backgroundColor}
                      $color={fontColor}
                      onClick={() => navigateTo(name)}
                      $fontType={fontType}
                      size="large"
                    >
                      <span className="icon">{BUTTON_ICONS[name]}</span> {value}
                    </HighlightButton>
                  ) : (
                    <HighlightButton
                      type="default"
                      ghost
                      block
                      className="menu-button additonal-link"
                      $highlightColor={highlightColor}
                      $backgroundColor={backgroundColor}
                      $color={fontColor}
                      $fontType={fontType}
                      href={value}
                      target="_blank"
                      size="large"
                      onClick={() => {
                        ga.event({ ...TRACKING.HOME.BUTTON.CUSTOMIZED_BUTTON, value });
                      }}
                    >
                      <span>{name}</span>
                    </HighlightButton>
                  )}
                </Col>
              )}
            </React.Fragment>
          ))}
        </Row>
      </div>
      <SocialMediaLinks micrositeData={micrositeData} />

      <PoweredByMomos color={fontColor} />
      <style jsx global>{`
        body {
          background: ${backgroundColor};
        }
      `}</style>
    </DesktopViewWrap>
  );
};

export default DesktopView;

const DesktopViewWrap = styled.div<DesktopViewWrapProps>`
  letter-spacing: ${(props) => (props.$fontType === FontsList[3].value ? '0.6px' : 'normal')};

  .hero-container {
    text-align: center;
    border-bottom: 1px solid ${(props) => props.$fontColor};
    padding: 30px 10px 20px 10px;
    background: ${(props) => props.$headerBgColor || props.$backgroundColor};

    .microsite-desk-logo {
      display: block;
      margin: 0 auto;
      width: 240px;
      height: 96px;
      object-fit: contain;
      margin-bottom: 10px;
    }

    .microsite-title {
      color: ${(props) => props.$headerTextColor || props.$fontColor};
      margin-bottom: 20px;
    }

    .header-text {
      color: ${(props) => props.$headerTextColor || props.$fontColor};
      fontfamily: ${(props) => props.$fontType};
    }

    .location-text {
      display: flex;
      flex-direction: row;
      align-items: center;
      text-align: center;
      justify-content: center;
      margin-bottom: 15px;
      cursor: pointer;

      svg {
        margin-right: 5px;

        path {
          stroke: ${(props) => props.$headerTextColor || props.$fontColor};
        }
      }
    }

    .service-options-wrap {
      display: flex;
      justify-content: center;

      .service-option {
        margin-right: 10px;
        align-items: center;
        display: flex;

        :before {
          content: '';
          display: inline-block;
          width: 10px;
          height: 10px;
          -moz-border-radius: 5px;
          -webkit-border-radius: 5px;
          border-radius: 5px;
          background-color: ${(props) => props.$headerTextColor || props.$fontColor};
          margin-right: 10px;
        }
      }
    }

    .rating-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      margin-bottom: 20px;
    }
  }

  .links-wrap {
    padding: 50px 100px;

    @media (max-width: ${breakpoints.tablet}) {
      padding: 20px 30px;
    }
    .ant-row {
      flex-flow: column;
    }
    .menu-button {
      white-space: pre-wrap !important;
      height: auto !important;
      min-height: 60px;
      min-width: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      flex-direction: row;
      width: 100%;
      padding: 1rem 3rem 1rem 3rem !important;
      svg {
        margin-right: 20px;
      }
      span {
        display: inline-block;
        max-width: 100%;
      }
      .icon {
        position: absolute;
        left: 15px;
      }
    }
  }

  .social-media-links {
    margin-bottom: 10px;
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    .footer {
      padding: 20px;

      .footer-title {
        text-align: center;
        margin-bottom: 30px;
        color: var(--white);
      }
    }
  }
`;

type HighlightButtonProps = {
  $color: string;
  $fontType: string;
  $backgroundColor: string;
  $highlightColor: string;
};

const HighlightButton = styled(Button)<HighlightButtonProps>`
  @media (min-width: ${breakpoints.laptopL}) {
    height: 60px !important;
  }

  border-radius: 30px;

  &.additonal-link {
    padding-bottom: 0.01px !important;
  }

  span {
    color: ${(props) => props.$color} !important;
    font-family: ${(props) => props.$fontType} !important;
    font-size: 16px;
    letter-spacing: ${(props) => (props.$fontType === FontsList[3].value ? '0.6px' : 'normal')};
    overflow-wrap: break-word !important;
  }

  & > span svg path {
    stroke: ${(props) => props.$color};
  }

  border-color: ${(props) => props.$color} !important;

  :hover {
    span {
      color: ${(props) => props.$backgroundColor} !important;
    }
    background: ${(props) => props.$highlightColor} !important;
    border-color: ${(props) => props.$highlightColor} !important;

    & > span svg path {
      stroke: ${(props) => props.$backgroundColor};
    }
  }
`;

const MicrositeSubscribeButton = styled.div<SubscribeButtonWrapProps>`
  display: flex;
  justify-content: flex-end;
  width: 75%;
  cursor: pointer;

  .btn-container {
    padding: 10px 16px;
    border: 1px solid ${(props) => props.fontColor};
    border-radius: 25px;
    display: flex;
    justify-content: center;
    gap: 10px;
    align-items: center;

    path {
      stroke: ${(props) => props.fontColor};
    }

    .btn-text {
      font-family: ${(props) => props.fontType};
      font-size: 14px;
      color: ${(props) => props.fontColor};
      margin-bottom: 0;
    }
    ::selection {
      background: ${(props) => props.highlightColor};
    }
    :hover {
      border: 1px solid ${(props) => props.highlightColor};

      path {
        stroke: ${(props) => props.highlightColor};
      }

      .btn-text {
        font-family: ${(props) => props.highlightColor};
        color: ${(props) => props.highlightColor};
      }
    }
  }

  .btn-container-disabled {
    padding: 10px 16px;
    border: 1px solid ${(props) => props.fontColor};
    border-radius: 25px;
    display: flex;
    justify-content: center;
    gap: 10px;
    align-items: center;

    path {
      stroke: ${(props) => props.fontColor};
    }

    .btn-text {
      font-family: ${(props) => props.fontType};
      font-size: 14px;
      color: ${(props) => props.fontColor};
      margin-bottom: 0;
    }
  }

  @media (max-width: ${breakpoints.mobileS}) {
    width: 100%;
  }
  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
  }
  @media (max-width: ${breakpoints.mobileL}) {
    width: 100%;
  }
  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
  }
`;
